/**
 * Workflow Mutation Manager
 *
 * Manages workflow state mutations in a reversible way for undo/redo support.
 * This is the ONLY place where workflow state should be mutated during command execution.
 *
 * ARCHITECTURE COMPLIANCE:
 * - Commands call this manager to mutate workflow state
 * - Manager updates Zustand store
 * - Full undo/redo support via change records
 */

import { useWorkflowStore, WorkflowState } from '../../../stores/workflowStore';
import { UseCaseType } from '../../../domains/genogram/models/UseCase';
import { WorkflowStep } from '../../../domains/genogram/models/Workflow';
import type { GenogramAssistantRole } from '../../../domains/genogram/models/AssistantRole';
import { Logger } from '../../../utils/logger';

const logger = Logger.getLoggerWithPrefix('WorkflowMutationManager');

export interface WorkflowChangeRecord {
  oldState: Partial<WorkflowState>;
  newState: Partial<WorkflowState>;
}

export class WorkflowMutationManager {
  /**
   * Update workflow state with partial changes
   * Returns a change record for undo/redo
   */
  updateWorkflow(changes: Partial<WorkflowState>): WorkflowChangeRecord {
    const store = useWorkflowStore.getState();

    // Build change record
    const oldState: Partial<WorkflowState> = {};
    const newState: Partial<WorkflowState> = {};

    Object.keys(changes).forEach(key => {
      const field = key as keyof WorkflowState;
      oldState[field] = store[field] as any;
      newState[field] = changes[field] as any;
    });

    // Apply changes to store
    useWorkflowStore.setState((state) => ({
      ...state,
      ...changes
    }));

    logger.debug('[WorkflowMutationManager] Updated workflow state', { changes });

    return { oldState, newState };
  }

  /**
   * Set the selected use case
   */
  setUseCase(useCaseId: UseCaseType | null): WorkflowChangeRecord {
    return this.updateWorkflow({ selectedUseCase: useCaseId });
  }

  /**
   * Set assistant role
   */
  setAssistantRole(role: GenogramAssistantRole | null): WorkflowChangeRecord {
    return this.updateWorkflow({ assistantRole: role });
  }

  /**
   * Set the current workflow step
   */
  setCurrentStep(step: WorkflowStep | null): WorkflowChangeRecord {
    return this.updateWorkflow({ currentStep: step });
  }

  /**
   * Add a step to the skipped list
   */
  skipStep(stepId: string): WorkflowChangeRecord {
    const current = useWorkflowStore.getState().skippedSteps;
    if (current.includes(stepId)) {
      return { oldState: {}, newState: {} };
    }
    return this.updateWorkflow({
      skippedSteps: [...current, stepId]
    });
  }

  /**
   * Remove a step from the skipped list
   */
  unskipStep(stepId: string): WorkflowChangeRecord {
    const current = useWorkflowStore.getState().skippedSteps;
    return this.updateWorkflow({
      skippedSteps: current.filter(id => id !== stepId)
    });
  }

  /**
   * Mark a step as completed
   */
  completeStep(stepId: string): WorkflowChangeRecord {
    const current = useWorkflowStore.getState().completedSteps;
    if (current.includes(stepId)) {
      return { oldState: {}, newState: {} };
    }
    return this.updateWorkflow({
      completedSteps: [...current, stepId]
    });
  }

  /**
   * Mark a step as not completed
   */
  uncompleteStep(stepId: string): WorkflowChangeRecord {
    const current = useWorkflowStore.getState().completedSteps;
    return this.updateWorkflow({
      completedSteps: current.filter(id => id !== stepId)
    });
  }

  /**
   * Reset workflow state (for use case changes)
   */
  resetWorkflow(): WorkflowChangeRecord {
    return this.updateWorkflow({
      currentStep: null,
      skippedSteps: [],
      completedSteps: []
    });
  }
}

// Singleton instance
let workflowMutationManagerInstance: WorkflowMutationManager | null = null;

export function getWorkflowMutationManager(): WorkflowMutationManager {
  if (!workflowMutationManagerInstance) {
    workflowMutationManagerInstance = new WorkflowMutationManager();
  }
  return workflowMutationManagerInstance;
}
